// src/pages/PreviewSubdeckPage.tsx
// Vitrine pública — página de um subdeck com preview de cards ou apostila
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Lock, ScrollText } from 'lucide-react';
import './PreviewSubdeckPage.css';

interface PreviewCard { front: string }

interface ApostilaPreview { title: string; html: string }

interface SubdeckDetail {
    subdeckId: string;
    subdeckName: string;
    subdeckSlug: string;
    deckName: string;
    deckSlug: string;
    cardCount: number;
    apostilaCount: number;
    type: string;
    previewCards: PreviewCard[];
    topicsFronts: string[];
    apostilaPreview: ApostilaPreview | null;
}

async function fetchSubdeckPreview(deckSlug: string, subdeckSlug: string): Promise<SubdeckDetail> {
    const res = await fetch(`/api/public/preview/${deckSlug}/${subdeckSlug}`);
    if (!res.ok) throw new Error('Submódulo não encontrado');
    return res.json();
}

const PreviewSubdeckPage: React.FC = () => {
    const { deckSlug, subdeckSlug } = useParams<{ deckSlug: string; subdeckSlug: string }>();
    const [searchParams] = useSearchParams();
    const ref = searchParams.get('ref');

    const { data: sd, isLoading, error } = useQuery({
        queryKey: ['public-preview-subdeck', deckSlug, subdeckSlug],
        queryFn: () => fetchSubdeckPreview(deckSlug!, subdeckSlug!),
        staleTime: 0, // sempre busca dado fresco — conteúdo de apostila muda com edições
        enabled: !!deckSlug && !!subdeckSlug,
    });

    if (isLoading) return <div className="cpv-loading-full">Carregando…</div>;
    if (error || !sd) return (
        <div className="cpv-error-full">
            <p>Submódulo não encontrado.</p>
            <Link to="/preview">← Voltar à vitrine</Link>
        </div>
    );

    const refQ = ref ? `?ref=${ref}` : '';
    const isApostila = sd.type === 'apostila';

    return (
        <div className="cpvs-root" data-theme="light">
            {/* Header */}
            <header className="cpvd-header">
                <div className="cpvd-header-inner">
                    <Link to={`/preview/${sd.deckSlug}${refQ}`} className="cpvd-back">
                        <ArrowLeft size={16} /> {sd.deckName}
                    </Link>
                    <a href="/" className="cpvd-logo">GRAIA</a>
                    <a href="/cadastro" className="cpvd-cta-header">Criar conta grátis</a>
                </div>
            </header>

            <div className="cpvd-layout">
                <main className="cpvd-main">
                    {/* Hero do subdeck */}
                    <div className="cpvs-hero">
                        <div className="cpvs-breadcrumb">
                            <Link to={`/preview${refQ}`}>Vitrine</Link>
                            <span>›</span>
                            <Link to={`/preview/${sd.deckSlug}${refQ}`}>{sd.deckName}</Link>
                            <span>›</span>
                            <span>{sd.subdeckName}</span>
                        </div>
                        <div className="cpvs-title-row">
                            {isApostila
                                ? <ScrollText size={24} className="cpvs-icon cpvs-icon-apostila" />
                                : <FileText size={24} className="cpvs-icon" />
                            }
                            <h1 className="cpvs-title">{sd.subdeckName}</h1>
                        </div>
                        <div className="cpvs-meta">
                            {isApostila
                                ? <span className="cpvd-meta-pill cpvd-meta-pill-apostila">Apostila</span>
                                : <span className="cpvd-meta-pill">{sd.cardCount} cards</span>
                            }
                            {!isApostila && sd.apostilaCount > 0 && (
                                <span className="cpvd-meta-pill green">Apostila</span>
                            )}
                        </div>
                    </div>

                    {/* ── Apostila: renderiza HTML da primeira página ── */}
                    {isApostila && sd.apostilaPreview && (
                        <div className="cpvs-apostila-page">
                            {sd.apostilaPreview.title && (
                                <div className="cpvs-apostila-page-title">{sd.apostilaPreview.title}</div>
                            )}
                            {sd.apostilaPreview.html && (
                                <div
                                    className="cpvs-apostila-content rr-content"
                                    dangerouslySetInnerHTML={{ __html: sd.apostilaPreview.html }}
                                />
                            )}
                            <div className="cpvs-apostila-cta-row">
                                <span className="cpvs-apostila-cta-label">Apostila completa disponível na plataforma</span>
                                <a href={`/cadastro${refQ}`} className="cpvs-apostila-cta-btn">Acessar completo</a>
                            </div>
                        </div>
                    )}

                    {isApostila && !sd.apostilaPreview && (
                        <div className="cpvs-apostila-empty">
                            <ScrollText size={40} opacity={0.2} />
                            <p>Apostila disponível na plataforma</p>
                            <a href={`/cadastro${refQ}`} className="cpvd-cta-primary" style={{ display: 'inline-block', marginTop: 12 }}>
                                Criar conta grátis
                            </a>
                        </div>
                    )}

                    {/* ── Flashcards: tópicos + preview ── */}
                    {!isApostila && sd.topicsFronts.length > 0 && (
                        <div className="cpvs-topics">
                            <h2 className="cpvs-section-title">O que você vai estudar</h2>
                            <ul className="cpvs-topics-list">
                                {sd.topicsFronts.map((t: string, i: number) => (
                                    <li key={i} className="cpvs-topic-item">
                                        <span className="cpvs-topic-bullet">→</span>
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {!isApostila && sd.previewCards.length > 0 && (
                        <div className="cpvs-cards-section">
                            <h2 className="cpvs-section-title">Prévia dos flashcards</h2>
                            <div className="cpvs-cards-list">
                                {sd.previewCards.map((c: PreviewCard, i: number) => (
                                    <div key={i} className="cpvs-card">
                                        <div className="cpvs-card-side cpvs-card-front">
                                            <span className="cpvs-card-label">Frente</span>
                                            <div
                                                className="cpvs-card-front-content"
                                                dangerouslySetInnerHTML={{ __html: c.front }}
                                            />
                                        </div>
                                        <div className="cpvs-card-side cpvs-card-back">
                                            <span className="cpvs-card-label">Verso</span>
                                            <div className="cpvs-card-locked">
                                                <Lock size={18} />
                                                <span>Disponível na plataforma</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {sd.cardCount > sd.previewCards.length && (
                                <p className="cpvs-more">
                                    + {sd.cardCount - sd.previewCards.length} cards adicionais disponíveis na plataforma
                                </p>
                            )}
                        </div>
                    )}
                </main>

                {/* Sidebar CTA */}
                <aside className="cpvd-sidebar">
                    <div className="cpvd-sidebar-card">
                        <h3>Acesso completo</h3>
                        <ul className="cpvd-benefits">
                            {isApostila
                                ? <li>✓ Apostila completa com todas as páginas</li>
                                : <li>✓ {sd.cardCount} flashcards com verso</li>
                            }
                            <li>✓ Repetição espaçada inteligente</li>
                            <li>✓ Progresso salvo na nuvem</li>
                            {sd.apostilaCount > 0 && !isApostila && <li>✓ Apostila completa</li>}
                            <li>✓ Todos os módulos de medicina</li>
                        </ul>
                        <a href={`/cadastro${refQ}`} className="cpvd-cta-primary">
                            Quero acesso completo
                        </a>
                        <a href="/login" className="cpvd-cta-link">Já tenho conta →</a>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default PreviewSubdeckPage;
